import { useEffect, useRef, useState } from "react";
import { confirmCrmExport, getCrmExport, previewCrmExport, retryCrmExport } from "../../api/integrationsApi";
import { crmRequestError, crmRequestErrorCode } from "../../helper/crmCatalog";
import { createSecureUuid } from "../../helper/secureUuid";

const REJECTED_PREVIEW_CODES = ["preview_stale", "portal_changed", "export_in_progress", "crm_reconnect_required", "crm_connection_changed"];

export default function useCrmExport({ open, savedSearchId, companyIds, initialExportId, initialUncertain = false, initialConfirmationKey = null }) {
  const selectionKey = JSON.stringify({ savedSearchId, companyIds: [...new Set(companyIds)].sort() });
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [errorCode, setErrorCode] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [pollVersion, setPollVersion] = useState(0);
  const [confirmationUncertain, setConfirmationUncertain] = useState(initialUncertain);
  const current = useRef(null);
  const generation = useRef(0);
  const actionPending = useRef(false);
  const [confirmationKey, setConfirmationKey] = useState(initialConfirmationKey);
  const startingExport = useRef({ selectionKey, exportId: initialExportId, uncertain: initialUncertain });
  const model = snapshot?.selectionKey === selectionKey ? snapshot.value : null;

  useEffect(() => {
    const requestGeneration = ++generation.current;
    actionPending.current = false;
    const invalidate = () => { generation.current = requestGeneration + 1; };
    if (!open) return invalidate;
    if (current.current?.selectionKey === selectionKey) { setBusy(""); return invalidate; }
    const resumedId = startingExport.current.selectionKey === selectionKey ? startingExport.current.exportId : null;
    setBusy(resumedId ? "checking" : "previewing");
    setError("");
    setErrorCode("");
    setConfirmationUncertain(Boolean(resumedId && startingExport.current.uncertain));
    setHistory([]);
    const request = resumedId ? getCrmExport(resumedId) : previewCrmExport(JSON.parse(selectionKey));
    request.then((value) => {
      if (requestGeneration !== generation.current) return;
      const next = { selectionKey, value };
      current.current = next;
      setSnapshot(next);
      if (value.status !== "previewed") setConfirmationUncertain(false);
    }).catch((requestError) => {
      if (requestGeneration === generation.current) setError(crmRequestError(requestError));
    }).finally(() => {
      if (requestGeneration === generation.current) setBusy("");
    });
    return invalidate;
  }, [open, selectionKey, previewVersion]);

  const exportId = model?.export_id;
  const status = model?.status;
  useEffect(() => {
    if (!open || !exportId || !["queued", "running"].includes(status)) return;
    let active = true;
    let timer;
    const poll = async () => {
      let delay = 1000;
      try {
        const value = await getCrmExport(exportId);
        if (!active) return;
        const next = { selectionKey, value };
        current.current = next;
        setSnapshot(next);
        setError("");
        if (!["queued", "running"].includes(value.status)) return;
      } catch (requestError) {
        if (!active) return;
        setError(crmRequestError(requestError, "Progress is temporarily unavailable. The accepted push may still be running; status checks will retry."));
        delay = 5000;
      }
      timer = setTimeout(poll, delay);
    };
    timer = setTimeout(poll, pollVersion > 0 ? 0 : 1000);
    return () => { active = false; clearTimeout(timer); };
  }, [open, exportId, status, selectionKey, pollVersion]);

  const confirm = async () => {
    const selected = current.current;
    if (actionPending.current || selected?.selectionKey !== selectionKey || selected.value.status !== "previewed") return;
    actionPending.current = true;
    const requestGeneration = generation.current;
    setBusy("confirming");
    setError("");
    setErrorCode("");
    setConfirmationUncertain(true);
    try {
      const key = confirmationKey?.exportId === selected.value.export_id
        ? confirmationKey : { exportId: selected.value.export_id, key: createSecureUuid() };
      setConfirmationKey(key);
      const value = await confirmCrmExport(selected.value.export_id, key.key);
      if (requestGeneration !== generation.current) return;
      const next = { selectionKey, value };
      current.current = next;
      setSnapshot(next);
      setConfirmationUncertain(false);
    } catch (requestError) {
      if (requestGeneration === generation.current) {
        setError(crmRequestError(requestError, "Confirmation could not be verified. Retry the same confirmation to safely recover its status."));
        const code = crmRequestErrorCode(requestError);
        setErrorCode(code);
        if (REJECTED_PREVIEW_CODES.includes(code)) setConfirmationUncertain(false);
      }
    } finally {
      if (requestGeneration === generation.current) { actionPending.current = false; setBusy(""); }
    }
  };

  const retry = async () => {
    const selected = current.current;
    if (actionPending.current || selected?.selectionKey !== selectionKey || !["completed_with_failures", "failed"].includes(selected.value.status)) return;
    actionPending.current = true;
    const requestGeneration = generation.current;
    setBusy("retrying");
    setError("");
    try {
      const value = await retryCrmExport(selected.value.export_id);
      if (requestGeneration !== generation.current) return;
      setHistory((previous) => [...previous, selected]);
      const next = { selectionKey, value };
      current.current = next;
      setSnapshot(next);
    } catch (requestError) {
      if (requestGeneration === generation.current) setError(crmRequestError(requestError));
    } finally {
      if (requestGeneration === generation.current) { actionPending.current = false; setBusy(""); }
    }
  };

  const checkStatus = async () => {
    const selected = current.current;
    if (actionPending.current || selected?.selectionKey !== selectionKey) return;
    if (["queued", "running"].includes(selected.value.status)) { setPollVersion((version) => version + 1); return; }
    actionPending.current = true;
    const requestGeneration = generation.current;
    setBusy("checking");
    try {
      const value = await getCrmExport(selected.value.export_id);
      if (requestGeneration !== generation.current) return;
      const next = { selectionKey, value };
      current.current = next;
      setSnapshot(next);
      setError("");
      setErrorCode("");
      if (value.status !== "previewed") setConfirmationUncertain(false);
    } catch (requestError) {
      if (requestGeneration === generation.current) setError(crmRequestError(requestError, "Push status could not be verified. Try checking status again."));
    } finally {
      if (requestGeneration === generation.current) { actionPending.current = false; setBusy(""); }
    }
  };

  const previewRejected = REJECTED_PREVIEW_CODES.includes(errorCode);
  const canNewPreview = !busy && !["queued", "running"].includes(model?.status) && !confirmationUncertain;
  const newPreview = () => {
    if (!canNewPreview) return;
    current.current = null;
    startingExport.current = { selectionKey, exportId: null };
    setConfirmationKey(null);
    setSnapshot(null);
    setPreviewVersion((version) => version + 1);
  };

  return { model, busy, error, confirm, retry, newPreview, canNewPreview, previewRejected, checkStatus, confirmationUncertain, confirmationKey,
    previousAttempts: history.filter((item) => item.selectionKey === selectionKey).map((item) => item.value) };
}
